import {useState, useEffect} from 'react';
import {
    getInquiriesWithStatus,
    getInquiryCounts,
    getUnopenedInquiryEventsCountBatch
} from "../APIs/inquiry";
import {useLocation, useNavigate} from "react-router-dom";

const useAuditorInquiries = (userRole, inquiriesPerPage) => {
    const [loading, setLoading] = useState(false);
    const [inquiryStatus, setInquiryStatus] = useState(userRole === 'auditor' ? 0 : 4);
    const [inquiries, setInquiries] = useState([]);
    const [totalInquiries, setTotalInquiries] = useState(0);
    const [inquiryCounts, setInquiryCounts] = useState({});
    const [updateCounts, setUpdateCounts] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({key: null, direction: null});
    const [searchCategory, setSearchCategory] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchPerformed, setIsSearchPerformed] = useState(false);

    const location = useLocation();
    const searchParams = location.state || {};
    const navigate = useNavigate();

    const fetchInquiries = async () => {
        setLoading(true);

        // Use values from state or location.state directly because of the async function
        const category = searchParams.searchCategory || searchCategory;
        const query = searchParams.searchQuery || searchQuery;

        if (searchParams.searchCategory && searchParams.searchQuery) {
            setSearchCategory(searchParams.searchCategory);
            setSearchQuery(searchParams.searchQuery);
        }
        // Clear the navigation state to prevent repeated searches on page refresh
        navigate(location.pathname, {replace: true});

        try {
            const filters = {
                inquiry_id: null,
                inquiry_subject: null,
                inquiry_creator: null,
                org_name: null,
                product_name: null,
                seller_name: null,
            };

            if (query.trim() !== "") {
                switch (category) {
                    case "id":
                        filters.inquiry_id = query;
                        break;
                    case "subject":
                        filters.inquiry_subject = query;
                        break;
                    case "fullname":
                        filters.inquiry_creator = query;
                        break;
                    case "org":
                        filters.org_name = query;
                        break;
                    case "product":
                        filters.product_name = query;
                        break;
                    case "seller":
                        filters.seller_name = query;
                        break;
                    default:
                        filters.inquiry_id = query;
                        filters.inquiry_subject = query;
                        filters.inquiry_creator = query;
                        filters.org_name = query;
                        filters.product_name = query;
                        filters.seller_name = query;
                        break;
                }
            } else {
                setIsSearchPerformed(false);
            }

            const sortKey = sortConfig.key === 3 ? "date" :
                sortConfig.key === 4 ? "fullname" :
                    sortConfig.key === 5 ? "org" : null;

            const inquiriesData = await getInquiriesWithStatus(
                inquiryStatus,
                currentPage,
                inquiriesPerPage,
                filters.inquiry_id,
                filters.inquiry_subject,
                filters.inquiry_creator,
                filters.org_name,
                filters.product_name,
                filters.seller_name,
                sortKey,
                sortConfig.direction
            );

            setInquiries(inquiriesData.data.inquiries);
            setTotalInquiries(inquiriesData.data.total_inquiries_count);

            if (userRole === 'auditor') {
                const {data: {inquiry_counts_by_status}} = await getInquiryCounts(filters);
                setInquiryCounts(inquiry_counts_by_status);

                const inquiryIds = inquiriesData.data.inquiries.map(inquiry => inquiry.id);
                const {data: counts} = await getUnopenedInquiryEventsCountBatch(inquiryIds);
                const countsData = {};
                counts.forEach(({inquiry_id, count}) => {
                    countsData[inquiry_id] = count;
                });
                setUpdateCounts(countsData);
            }
        } catch (error) {
            console.error("Error fetching inquiries:", error);
            setInquiries([]);
            setUpdateCounts({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInquiries();
    }, [inquiryStatus, currentPage, inquiriesPerPage, sortConfig]);

    return {
        loading,
        inquiryStatus,
        setInquiryStatus,
        inquiries,
        totalInquiries,
        inquiryCounts,
        updateCounts,
        currentPage,
        setCurrentPage,
        sortConfig,
        setSortConfig,
        searchCategory,
        setSearchCategory,
        searchQuery,
        setSearchQuery,
        isSearchPerformed,
        setIsSearchPerformed,
        fetchInquiries
    };
};

export default useAuditorInquiries;